import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiRepeat, FiSearch, FiChevronDown, FiChevronRight, FiExternalLink } from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { formatDate as fmtDate } from '../../utils/formatters';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import { getTransactions } from '../../utils/transactionHelper';
import { navigateToTransaction } from '../../utils/navigationHelper';
import useReportMemory from '../../hooks/useReportMemory';

const formatDate = (v) => fmtDate(v) || '-';
const num = (n) => Number(n || 0).toLocaleString('en-PK');

const STATUS_COLORS = { Draft: colors.warning.main, Sent: colors.info.main, 'Partially Received': '#FF8C00', Received: colors.success.main, Cancelled: colors.error.main };

const TransferReport = () => {
  const navigate = useNavigate();
  const { activeCompany, activeFinancialYear } = useAuth();
  const [savedMem, saveMemory] = useReportMemory('transfer-report');

  const [sends, setSends] = useState(() => savedMem?.sends ?? []);
  const [receives, setReceives] = useState(() => savedMem?.receives ?? []);
  const [hasGenerated, setHasGenerated] = useState(() => savedMem?.hasGenerated ?? false);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  // Filters
  const [dateFrom, setDateFrom] = useState(() => savedMem?.dateFrom ?? '');
  const [dateTo, setDateTo] = useState(() => savedMem?.dateTo ?? '');
  const [fromBranch, setFromBranch] = useState(() => savedMem?.fromBranch ?? 'all');
  const [toBranch, setToBranch] = useState(() => savedMem?.toBranch ?? 'all');
  const [fromLocation, setFromLocation] = useState(() => savedMem?.fromLocation ?? 'all');
  const [toLocation, setToLocation] = useState(() => savedMem?.toLocation ?? 'all');
  const [statusFilter, setStatusFilter] = useState(() => savedMem?.statusFilter ?? 'all');
  const [varianceFilter, setVarianceFilter] = useState(() => savedMem?.varianceFilter ?? 'all'); // all | mismatched | matched
  const [itemFilter, setItemFilter] = useState(() => savedMem?.itemFilter ?? 'all');
  const [senderFilter, setSenderFilter] = useState(() => savedMem?.senderFilter ?? 'all');
  const [search, setSearch] = useState(() => savedMem?.search ?? '');

  const generate = async () => {
    if (!activeCompany?.id || !activeFinancialYear?.id) return;
    setLoading(true);
    try {
      const [snd, rcv] = await Promise.all([
        getTransactions(activeCompany.id, { tranTypeCode: 'SND', financialYearId: activeFinancialYear.id }),
        getTransactions(activeCompany.id, { tranTypeCode: 'RCV', financialYearId: activeFinancialYear.id })
      ]);
      setSends(snd || []);
      setReceives(rcv || []);
      setHasGenerated(true);
    } catch (e) { console.error('Transfer report load failed:', e); alert('Failed to load: ' + e.message); }
    finally { setLoading(false); }
  };

  // Dropdown option lists (derived from loaded data).
  const opts = useMemo(() => {
    const fb = new Set(), tb = new Set(), fl = new Set(), tl = new Set(), it = new Map(), sndr = new Set();
    sends.forEach(s => {
      if (s.fromBranchName) fb.add(s.fromBranchName);
      if (s.toBranchName) tb.add(s.toBranchName);
      if (s.fromLocationName) fl.add(s.fromLocationName);
      if (s.toLocationName) tl.add(s.toLocationName);
      if (s.employeeName) sndr.add(s.employeeName);
      (s.lines || []).forEach(l => { if (l.itemId) it.set(l.itemId, l.itemName || l.description || l.itemId); });
    });
    return {
      fromBranches: [...fb].sort(), toBranches: [...tb].sort(), fromLocations: [...fl].sort(),
      toLocations: [...tl].sort(), senders: [...sndr].sort(),
      items: [...it.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name))
    };
  }, [sends]);

  // One row per Transfer (Send), with its receives folded in + per-item variance.
  const rows = useMemo(() => {
    // index receives by their parent Send
    const rcvBySend = {};
    receives.forEach(r => {
      const key = r.parentId || r.parentDocNumber;
      if (!key) return;
      (rcvBySend[r.parentId] = rcvBySend[r.parentId] || []).push(r);
      if (r.parentDocNumber) (rcvBySend[r.parentDocNumber] = rcvBySend[r.parentDocNumber] || []).push(r);
    });

    return sends.map(snd => {
      const rcvs = rcvBySend[snd.id] || rcvBySend[snd.docNumber] || [];
      // per-item sent / received
      const byItem = {};
      (snd.lines || []).forEach(l => {
        if (!l.itemId) return;
        const e = byItem[l.itemId] || (byItem[l.itemId] = { itemId: l.itemId, itemName: l.itemName || l.description || l.itemId, sent: 0, received: 0 });
        e.sent += Number(l.quantity) || 0;
      });
      rcvs.forEach(r => (r.lines || []).forEach(l => {
        if (!l.itemId) return;
        const e = byItem[l.itemId] || (byItem[l.itemId] = { itemId: l.itemId, itemName: l.itemName || l.description || l.itemId, sent: 0, received: 0 });
        e.received += Number(l.quantity) || 0;
      }));
      const itemRows = Object.values(byItem).map(e => ({ ...e, diff: e.sent - e.received }));
      const totalSent = itemRows.reduce((s, e) => s + e.sent, 0);
      const totalReceived = itemRows.reduce((s, e) => s + e.received, 0);
      return {
        snd, receives: rcvs, itemRows,
        totalSent, totalReceived, diff: totalSent - totalReceived,
        itemIds: new Set(Object.keys(byItem))
      };
    }).filter(Boolean);
  }, [sends, receives]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(r => {
      const s = r.snd;
      if (dateFrom && (s.docDate || '') < dateFrom) return false;
      if (dateTo && (s.docDate || '') > dateTo) return false;
      if (fromBranch !== 'all' && s.fromBranchName !== fromBranch) return false;
      if (toBranch !== 'all' && s.toBranchName !== toBranch) return false;
      if (fromLocation !== 'all' && s.fromLocationName !== fromLocation) return false;
      if (toLocation !== 'all' && s.toLocationName !== toLocation) return false;
      if (statusFilter !== 'all' && s.status !== statusFilter) return false;
      if (varianceFilter === 'mismatched' && r.diff === 0) return false;
      if (varianceFilter === 'matched' && r.diff !== 0) return false;
      if (itemFilter !== 'all' && !r.itemIds.has(itemFilter)) return false;
      if (senderFilter !== 'all' && s.employeeName !== senderFilter) return false;
      if (q && !(`${s.docNumber} ${s.toBranchName || ''} ${s.fromLocationName || ''} ${s.toLocationName || ''}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [rows, dateFrom, dateTo, fromBranch, toBranch, fromLocation, toLocation, statusFilter, varianceFilter, itemFilter, senderFilter, search]);

  const totals = useMemo(() => ({
    transfers: filteredRows.length,
    sent: filteredRows.reduce((s, r) => s + r.totalSent, 0),
    received: filteredRows.reduce((s, r) => s + r.totalReceived, 0),
    diff: filteredRows.reduce((s, r) => s + r.diff, 0)
  }), [filteredRows]);

  // Backward memory — keep the snapshot in sync once generated.
  useEffect(() => {
    if (!hasGenerated) return;
    saveMemory({
      sends, receives, hasGenerated: true,
      dateFrom, dateTo, fromBranch, toBranch, fromLocation, toLocation,
      statusFilter, varianceFilter, itemFilter, senderFilter, search
    });
  }, [hasGenerated, sends, receives, dateFrom, dateTo, fromBranch, toBranch, fromLocation,
      toLocation, statusFilter, varianceFilter, itemFilter, senderFilter, search, saveMemory]);

  const toggle = useCallback((id) => setExpanded(p => ({ ...p, [id]: !p[id] })), []);
  const openSend = (snd) => navigateToTransaction(navigate, 'SND', snd.id);
  const openReceive = (r) => navigateToTransaction(navigate, 'RCV', r.id);

  return (
    <PageLayout pageTitle="Transfer Report" breadcrumb="Reports / Transfer Report">
      <Panel>
        <PanelTitle><FiRepeat /> Transfer Report — Send · Receive · Variance</PanelTitle>
        <PanelSub>One row per transfer. Expand to see per-item Sent / Received / Difference. Un-received transfers show the full quantity as the difference (in-transit).</PanelSub>
        <Filters>
          <Field><label>From date</label><input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} /></Field>
          <Field><label>To date</label><input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} /></Field>
          <Field><label>From branch</label><select value={fromBranch} onChange={e => setFromBranch(e.target.value)}><option value="all">All</option>{opts.fromBranches.map(b => <option key={b} value={b}>{b}</option>)}</select></Field>
          <Field><label>To branch</label><select value={toBranch} onChange={e => setToBranch(e.target.value)}><option value="all">All</option>{opts.toBranches.map(b => <option key={b} value={b}>{b}</option>)}</select></Field>
          <Field><label>From location</label><select value={fromLocation} onChange={e => setFromLocation(e.target.value)}><option value="all">All</option>{opts.fromLocations.map(b => <option key={b} value={b}>{b}</option>)}</select></Field>
          <Field><label>To location</label><select value={toLocation} onChange={e => setToLocation(e.target.value)}><option value="all">All</option>{opts.toLocations.map(b => <option key={b} value={b}>{b}</option>)}</select></Field>
          <Field><label>Status</label><select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}><option value="all">All</option><option>Sent</option><option>Partially Received</option><option>Received</option><option>Cancelled</option></select></Field>
          <Field><label>Variance</label><select value={varianceFilter} onChange={e => setVarianceFilter(e.target.value)}><option value="all">All</option><option value="mismatched">Only mismatched</option><option value="matched">Fully matched</option></select></Field>
          <Field><label>Item</label><select value={itemFilter} onChange={e => setItemFilter(e.target.value)}><option value="all">All</option>{opts.items.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}</select></Field>
          <Field><label>Sender</label><select value={senderFilter} onChange={e => setSenderFilter(e.target.value)}><option value="all">All</option>{opts.senders.map(s => <option key={s} value={s}>{s}</option>)}</select></Field>
          <Field style={{ flex: 1, minWidth: 180 }}><label>Search Transfer No</label><input value={search} onChange={e => setSearch(e.target.value)} placeholder="Transfer no / location" /></Field>
          <GenBtn onClick={generate} disabled={loading}><FiSearch /> {loading ? 'Loading…' : 'Generate'}</GenBtn>
        </Filters>
      </Panel>

      {hasGenerated && (
        <Panel>
          <KpiRow>
            <Kpi><span>Transfers</span><strong>{totals.transfers}</strong></Kpi>
            <Kpi $c={colors.info.main}><span>Sent qty</span><strong>{num(totals.sent)}</strong></Kpi>
            <Kpi $c={colors.success.main}><span>Received qty</span><strong>{num(totals.received)}</strong></Kpi>
            <Kpi $c={totals.diff ? colors.error.main : colors.success.main}><span>Difference</span><strong>{num(totals.diff)}</strong></Kpi>
          </KpiRow>
          <TableWrap>
            <Table>
              <thead><tr>
                <Th style={{ width: 30 }}></Th><Th>Transfer No</Th><Th>Date</Th><Th>From</Th><Th>To</Th><Th>Sender</Th>
                <Th style={{ textAlign: 'right' }}>Sent</Th><Th style={{ textAlign: 'right' }}>Received</Th><Th style={{ textAlign: 'right' }}>Diff</Th><Th style={{ textAlign: 'center' }}>Status</Th>
              </tr></thead>
              <tbody>
                {filteredRows.length === 0 ? (
                  <tr><Td colSpan={10} style={{ textAlign: 'center', color: colors.text.tertiary, padding: 32 }}>No transfers match.</Td></tr>
                ) : filteredRows.map(r => {
                  const s = r.snd; const isOpen = !!expanded[s.id];
                  return (
                    <React.Fragment key={s.id}>
                      <Row onClick={() => toggle(s.id)}>
                        <Td>{isOpen ? <FiChevronDown /> : <FiChevronRight />}</Td>
                        <Td><Link onClick={(e) => { e.stopPropagation(); openSend(s); }}>{s.docNumber} <FiExternalLink size={10} /></Link></Td>
                        <Td>{formatDate(s.docDate)}</Td>
                        <Td>{`${s.fromBranchName ? s.fromBranchName + ' / ' : ''}${s.fromLocationName || '-'}`}</Td>
                        <Td>{`${s.toBranchName ? s.toBranchName + ' / ' : ''}${s.toLocationName || '-'}`}</Td>
                        <Td>{s.employeeName || '-'}</Td>
                        <Td style={{ textAlign: 'right' }}>{num(r.totalSent)}</Td>
                        <Td style={{ textAlign: 'right' }}>{num(r.totalReceived)}</Td>
                        <Td style={{ textAlign: 'right', color: r.diff ? colors.error.main : colors.text.secondary, fontWeight: r.diff ? 700 : 400 }}>{num(r.diff)}</Td>
                        <Td style={{ textAlign: 'center' }}><Badge $c={STATUS_COLORS[s.status]}>{s.status}</Badge></Td>
                      </Row>
                      {isOpen && (
                        <tr><Td colSpan={10} style={{ background: colors.neutral[50], padding: 0 }}>
                          <Detail>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                              <thead><tr><DTh>Item</DTh><DTh style={{ textAlign: 'right' }}>Sent</DTh><DTh style={{ textAlign: 'right' }}>Received</DTh><DTh style={{ textAlign: 'right' }}>Difference</DTh></tr></thead>
                              <tbody>
                                {r.itemRows.map(it => (
                                  <tr key={it.itemId}>
                                    <DTd>{it.itemName}</DTd>
                                    <DTd style={{ textAlign: 'right' }}>{num(it.sent)}</DTd>
                                    <DTd style={{ textAlign: 'right' }}>{num(it.received)}</DTd>
                                    <DTd style={{ textAlign: 'right', color: it.diff ? colors.error.main : colors.text.secondary, fontWeight: it.diff ? 700 : 400 }}>{num(it.diff)}</DTd>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            <RcvList>
                              {r.receives.length === 0 ? <em style={{ color: colors.text.tertiary, fontSize: 12 }}>Not received yet (in transit).</em>
                                : r.receives.map(rc => (
                                  <Link key={rc.id} onClick={() => openReceive(rc)} style={{ fontSize: 12 }}>{rc.docNumber} · {formatDate(rc.docDate)} <FiExternalLink size={10} /></Link>
                                ))}
                            </RcvList>
                          </Detail>
                        </Td></tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </Table>
          </TableWrap>
        </Panel>
      )}
    </PageLayout>
  );
};

const Panel = styled.div`background: white; border: 1px solid ${colors.neutral[200]}; border-radius: 10px; padding: 16px 20px; margin: 16px 20px 0;`;
const PanelTitle = styled.div`font-size: 15px; font-weight: 600; color: ${colors.text.primary}; display: flex; align-items: center; gap: 8px;`;
const PanelSub = styled.div`font-size: 12px; color: ${colors.text.secondary}; margin: 4px 0 12px;`;
const Filters = styled.div`display: flex; flex-wrap: wrap; gap: 10px; align-items: flex-end;`;
const Field = styled.div`display: flex; flex-direction: column; gap: 3px; label{font-size: 11px; color: ${colors.text.secondary};} input, select{padding: 7px 9px; border: 1px solid ${colors.neutral[300]}; border-radius: 7px; font-size: 13px; min-width: 130px;}`;
const GenBtn = styled.button`display: flex; align-items: center; gap: 6px; padding: 8px 16px; background: ${colors.primary.main}; color: white; border: none; border-radius: 7px; font-size: 13px; font-weight: 600; cursor: pointer; &:disabled{opacity: .5;}`;
const KpiRow = styled.div`display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 12px;`;
const Kpi = styled.div`flex: 1; min-width: 130px; border: 1px solid ${colors.neutral[200]}; border-left: 3px solid ${p => p.$c || colors.primary.main}; border-radius: 8px; padding: 10px 12px; span{display: block; font-size: 11px; color: ${colors.text.secondary};} strong{font-size: 18px; font-family: monospace;}`;
const TableWrap = styled.div`overflow-x: auto;`;
const Table = styled.table`width: 100%; border-collapse: collapse; font-size: 13px;`;
const Th = styled.th`text-align: left; padding: 8px 10px; font-size: 11px; font-weight: 600; color: ${colors.text.secondary}; text-transform: uppercase; border-bottom: 2px solid ${colors.neutral[200]}; white-space: nowrap;`;
const Td = styled.td`padding: 8px 10px; border-bottom: 1px solid ${colors.neutral[100]}; color: ${colors.text.primary};`;
const Row = styled.tr`cursor: pointer; &:hover td{background: ${colors.neutral[50]};}`;
const Badge = styled.span`display: inline-block; padding: 2px 10px; border-radius: 12px; font-size: 11px; font-weight: 600; background: ${p => (p.$c || colors.neutral[400]) + '20'}; color: ${p => p.$c || colors.text.secondary};`;
const Link = styled.span`color: ${colors.primary.main}; font-weight: 600; cursor: pointer; &:hover{text-decoration: underline;}`;
const Detail = styled.div`padding: 10px 16px;`;
const DTh = styled.th`text-align: left; padding: 5px 8px; font-size: 11px; color: ${colors.text.tertiary}; border-bottom: 1px solid ${colors.neutral[200]};`;
const DTd = styled.td`padding: 5px 8px; font-size: 12px; border-bottom: 1px solid ${colors.neutral[100]};`;
const RcvList = styled.div`display: flex; flex-wrap: wrap; gap: 12px; margin-top: 8px;`;

export default TransferReport;
